import type { DocNode } from '@atlaskit/adf-schema/doc';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { Schema } from '@atlaskit/editor-prosemirror/model';

import type { StreamingCommand } from '../../aiStreamingOrchestratorPluginType';

/**
 * Updates insert after docs by adding a local id of depth=1 if it doesn't exist (and returning the local id)
 * This is for the command queue to "correct" insertAfter commands if they are using the same local id to use the
 * end of the previous command.
 */
export function updateInsertAfterDoc(
	adfResponse: DocNode | DocNode['content'][number] | undefined,
	type: StreamingCommand['type'] | undefined,
	schema: Schema | undefined,
): {
	localId: string | undefined;
	updatedResponse: DocNode | DocNode['content'][number] | undefined;
} {
	if (!adfResponse || type !== 'insertNodeAfter') {
		return { updatedResponse: adfResponse, localId: undefined };
	}
	const indexMap: number[] = [];
	let newLocalId = crypto.randomUUID();
	const depthToCheckAgainst = 0;

	// First pass just to get the node to update
	traverse(adfResponse, {
		any: (_n, _p, index, depth) => {
			if (depth === depthToCheckAgainst) {
				indexMap.push(index);
			}
		},
	});

	// Update the localId of the last node
	let localIdUpdated = false;
	const newAdfResponse = traverse(
		{ ...adfResponse },
		{
			any: (node, _p, index, depth) => {
				if (
					depth === depthToCheckAgainst &&
					indexMap[indexMap.length - 1] === index &&
					!!schema?.nodes[node.type]?.spec?.attrs?.localId
				) {
					localIdUpdated = true;
					if (!!node?.attrs?.localId) {
						newLocalId = node?.attrs?.localId;
					}
					return { ...node, attrs: { ...node.attrs, localId: newLocalId } };
				}
			},
		},
	) as DocNode;

	return {
		updatedResponse: newAdfResponse ? newAdfResponse : adfResponse,
		localId: localIdUpdated ? newLocalId : undefined,
	};
}
